import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useLazyQuery, useMutation } from "@apollo/client";
import "./eventDetailPopup.css";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import PhoneIcon from "@mui/icons-material/Phone";
import MapIcon from "@mui/icons-material/Map";
import DescriptionIcon from "@mui/icons-material/Description";
import EditIcon from "@mui/icons-material/Edit";
import PersonIcon from "@mui/icons-material/Person";
import BdrCheckbox from "../../../../components/BdrCheckbox/BdrCheckbox";
import { SET_CALENDAR_ITEM_AVAILABLE_SOONER } from "../../../../utils/GraphQL/mutations";
import { formatCustomerAddress } from "../../../../utils/formatters/customerAddress";
import { useSnackbar } from "../../../../utils/GlobalSnackbar/GlobalSnackbarContext";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";
import BAGroupTimeFormat from "../../../../utils/formatters/BAGroupTimeFormat";
import { priceStrFormatter } from "../../../../utils/formatters/priceStrFormatter";
import LinkifiedText from "../../../../components/LinkifiedText/LinkifiedText";
import {
  getBlockLabelPrefix,
  getBlockLabelName,
  personalEventColor,
} from "../../../../utils/calendarLaneLayout";
import { isPersonalItem, isAllDayItem } from "../../../../utils/calendarPersonalEvents";
import { QUERY_CLIENT_PHOTOS } from "../../../../utils/GraphQL/queries";
import ImageModal from "../ImageModal/ImageModal";
import EventPhotoCell from "./EventPhotoCell";
import DriveTimeButton from "../DriveTime/DriveTimeButton";
import { makePlace } from "../../../../utils/DriveTimePin/DriveTimePinContext";
import { DEMO_LEGS } from "../DriveTime/driveTimeDemo";
import { useTourAction } from "../../../../tours/tourActions";

/* The walkthrough's "open the compare dialog" action, registered only by the
   DEMO popup (a component, so the hook is unconditional while the real popups
   never register it). Literal id: tourRegistry.test.js greps for it. */
const DemoCompareAction = ({ onRun }) => {
  useTourAction("drive-time-demo-compare", onRun);
  return null;
};

// Personal ("SIX") all-day rows carry a noon anchor and no endMs; the popup
// shows "All Day" + the date instead of a clock range.
const formatAllDayDate = (msStr) => {
  const n = parseInt(msStr, 10);
  if (Number.isNaN(n)) return "";
  return new Date(n).toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

// ---------------------------------------------------------------------------
// Pure display helpers — previously triplicated across WeekModal/DayModal/
// DayViewModal alongside three byte-identical copies of this popup. Now they
// live here with the single shared popup so the calendar surfaces stay in sync.
// ---------------------------------------------------------------------------
// The join itself now lives in utils/formatters/customerAddress.js — the Tasks
// console shows the same customer's address and offers a copy button for it,
// and two copies of this join is how the two screens drift apart.
const getCustomerAddress = (item) => formatCustomerAddress(item.customer);

const getCustomerPhone = (item) => {
  const phoneIds = item.customer?.phoneNumberIds;
  return phoneIds?.length > 0 ? phoneIds[0].phoneNumber : null;
};

const getAssignedNames = (item) => {
  return (item.assignedUsers || [])
    .map((u) => `${u.firstName || ""} ${u.lastName || ""}`.trim())
    .filter(Boolean)
    .join(" & ");
};

const formatPhoneForTel = (phone) => {
  if (!phone) return "";
  const digits = phone.replace(/\D/g, "");
  return digits.length >= 10 ? digits.substring(0, 10) : digits;
};

const getGoogleMapsUrl = (address) =>
  `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
    address
  )}`;

const getGoogleMapsEmbedUrl = (address) =>
  `https://maps.google.com/maps?q=${encodeURIComponent(
    address
  )}&z=10&output=embed`;

// Displayed dollar amount: invoice total with tip line items stripped, falling
// back to the raw quote total for quote-only events. Returns null on $0-net so
// the title suppresses the "- $0.00" suffix. (Mirrors the block-pill logic.)
const getJobAmountStr = (item) => {
  if (item.invoice?.invoiceItems) {
    const totalCents = parseInt(item.invoice.invoiceTotal) || 0;
    const tipCents = (item.invoice.invoiceItems || []).reduce((sum, it) => {
      if ((it.itemName || "").trim().toLowerCase() !== "tip") return sum;
      return sum + (parseInt(it.price) || 0) * (parseInt(it.quantity) || 1);
    }, 0);
    const displayCents = Math.max(totalCents - tipCents, 0);
    return displayCents > 0 ? priceStrFormatter(String(displayCents)) : null;
  }
  const cents = item.quote?.quoteTotal;
  return cents ? priceStrFormatter(cents) : null;
};

/**
 * Shared event-details popup for every calendar surface (week / 3-day / day /
 * standalone-day). Renders centered; when the customer has door photos a
 * read-only 2-up scrolling gallery is added to the right (modal stays centered,
 * the pair grows rightward). Click a photo to enlarge in ImageModal.
 *
 * Props:
 *   item          — selected event (calendarItem + customer + invoice/quote …)
 *   onClose       — close the popup
 *   onEdit        — (item) => open the edit-event modal
 *   onViewInvoice — (invoice) => open the invoice modal
 *   onViewQuote   — (quote) => open the quote modal
 *   tourDemo      — (registry §17.10) this is the walkthrough's demo visit:
 *                   the compare dialog shows canned legs and the tour's
 *                   "open the dialog" action is registered here.
 */
const EventDetailPopup = ({
  item,
  onClose,
  onEdit,
  onViewInvoice,
  onViewQuote,
  tourDemo = false,
}) => {
  const navigate = useNavigate();
  const { handleSnack } = useSnackbar();
  const [photos, setPhotos] = useState([]);
  // The enlarged photo and its note (the caption under it), or null.
  const [enlarge, setEnlarge] = useState(null);
  const openCompareRef = useRef(null);
  const [getImages] = useLazyQuery(QUERY_CLIENT_PHOTOS, {
    fetchPolicy: "network-only",
  });

  /*
   * Available Sooner — local mirror so the box responds on click.
   *
   * The popup's `item` comes from its host's last fetch, so waiting for a
   * refetch would leave the checkbox visibly lagging the click. Seeded from
   * the item and re-seeded whenever a different event is shown.
   */
  const [availableSooner, setAvailableSooner] = useState(
    !!item?.calendarItem?.availableSooner
  );
  const [setAvailSooner] = useMutation(SET_CALENDAR_ITEM_AVAILABLE_SOONER);

  // job / go_by only — the server refuses anything else, so a box that could
  // only ever error is not drawn.
  const asapEligible = ["job", "go_by"].includes(item?.calendarItem?.eventType);

  const customerId = item?.customer?._id;
  const photoIds = item?.customer?.clientPhotoIds;
  // Stable dependency key so the fetch only re-fires when the actual id set
  // changes, not on every parent re-render (item identity is unstable).
  const photoKey = Array.isArray(photoIds) ? photoIds.join(",") : "";

  useEffect(() => {
    setAvailableSooner(!!item?.calendarItem?.availableSooner);
  }, [item?.calendarItem?.id, item?.calendarItem?.availableSooner]);

  const handleAvailSoonerToggle = async (event) => {
    const next = event.target.checked;
    const previous = availableSooner;
    setAvailableSooner(next);

    try {
      await setAvailSooner({
        variables: { itemId: item.calendarItem.id, availableSooner: next },
      });
    } catch (err) {
      // Put the box back where it was: a checkbox that stays ticked after a
      // failed write is a lie about what the server holds. And SAY so — a box
      // that silently flips back looks like a misclick, not a failure.
      setAvailableSooner(previous);
      handleSnack(err?.message || "Could not update Available Sooner", 3500);
      console.error("Available Sooner toggle failed:", err?.message || err);
    }
  };

  useEffect(() => {
    if (!photoKey) {
      setPhotos([]);
      return;
    }
    let active = true;
    getImages({ variables: { pictureIdArr: photoKey.split(",") } }).then(
      ({ data }) => {
        // Keep only photos that actually came back with a usable image URL —
        // stale/orphaned ids in clientPhotoIds resolve to nothing, and we don't
        // want an empty panel (or a lone "Door Photos" title) to show for them.
        const usable = (data?.getImageById || []).filter(
          (p) => p?.s3ImageUrl || p?.s3ThumbUrl
        );
        if (active) setPhotos(usable);
      }
    );
    return () => {
      active = false;
    };
  }, [photoKey, getImages]);

  if (!item) return null;

  // Personal ("SIX") event, 2026-09-11: label title in the creator's colour,
  // "All Day" or the clock range + date, Edit action, note. No customer /
  // address / assigned / map / photos — none exist for a personal row.
  if (isPersonalItem(item)) {
    const ci = item.calendarItem;
    const allDay = isAllDayItem(item);
    return createPortal(
      <div className="day-detail-overlay" onClick={onClose}>
        <div className="event-detail-shell" onClick={(e) => e.stopPropagation()}>
          <div className="day-detail-popup">
            <div className="day-detail-header">
              <span
                className="day-detail-name"
                style={{ color: personalEventColor(item) }}
              >
                {getBlockLabelName(item) || "Personal"}
              </span>
              <IconButton onClick={onClose} size="small" sx={{ color: "#fff" }}>
                <CloseIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </div>

            <div className="day-detail-time">
              {allDay
                ? `All Day · ${formatAllDayDate(ci.startMs)}`
                : `${BAGroupTimeFormat(ci.startMs, true, true)}${
                    ci.endMs ? ` - ${BAGroupTimeFormat(ci.endMs, true, false)}` : ""
                  }`}
            </div>

            {/* The repeat line is composed SERVER-side (recurrenceText) so the
                wording cannot drift between this app and the phone. */}
            {(ci.recurrenceText || ci.recurrenceInvalid) && (
              <div
                style={{
                  fontSize: 12,
                  color: ci.recurrenceInvalid ? "#ffe082" : "#b0b0cc",
                  marginTop: 2,
                }}
              >
                {ci.recurrenceInvalid
                  ? "Repeats — schedule needs fixing"
                  : ci.recurrenceText}
              </div>
            )}

            {/* This occurrence carries its own details (2026-09-25, phase B):
                a "Only this event" save. Its fields are already merged into
                this row; the marker says why it differs from the rest. */}
            {ci.occurrenceOverridden && (
              <div style={{ fontSize: 12, color: "#b0b0cc", marginTop: 2 }}>
                This event has its own details
              </div>
            )}

            {ci.note && ci.note.trim() && (
              <div className="day-detail-notes" style={{ marginTop: 4 }}>
                <div style={{ fontSize: 12, color: "#b0b0cc", marginBottom: 2 }}>
                  Note:
                </div>
                <div
                  style={{
                    fontSize: 12,
                    color: "rgba(255,255,255,0.6)",
                    paddingLeft: 4,
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {/* URLs in a note open in a new tab (2026-09-26). */}
                  <LinkifiedText text={ci.note} linkStyle={{ color: "#8ab4f8" }} />
                </div>
              </div>
            )}

            <div className="day-detail-actions">
              <button title="Edit Event" onClick={() => onEdit(item)}>
                <EditIcon sx={{ fontSize: 20 }} />
              </button>
            </div>
          </div>
        </div>
      </div>,
      document.body
    );
  }

  const address = getCustomerAddress(item);
  const phone = getCustomerPhone(item);
  const assigned = getAssignedNames(item);
  const amount = getJobAmountStr(item);

  return createPortal(
    <>
      <div className="day-detail-overlay" onClick={onClose}>
        <div className="event-detail-shell" onClick={(e) => e.stopPropagation()}>
        <div className="day-detail-popup">
          <div className="day-detail-header">
            <span
              className="day-detail-name"
              style={{
                color: item.assignedUsers?.[0]?.defaultColor || "#fff",
              }}
            >
              {getBlockLabelPrefix(item.calendarItem)}
              {getBlockLabelName(item)}
              {amount && ` - ${amount}`}
            </span>
            <IconButton onClick={onClose} size="small" sx={{ color: "#fff" }}>
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </div>

          <div className="day-detail-time">
            {BAGroupTimeFormat(item.calendarItem.startMs, true, true)}
            {item.calendarItem.endMs &&
              ` - ${BAGroupTimeFormat(item.calendarItem.endMs, true, false)}`}
          </div>

          {address && <div className="day-detail-address">{address}</div>}

          {assigned && (
            <div className="day-detail-assigned">Assigned: {assigned}</div>
          )}

          {item.calendarItem.priorityNotes?.length > 0 && (
            <div className="day-detail-notes">
              {item.calendarItem.priorityNotes.map((note, i) => (
                <div key={i} className="day-detail-note-row">
                  <PriorityHighIcon sx={{ fontSize: 14, color: "#ff5252" }} />
                  <span>{note}</span>
                </div>
              ))}
            </div>
          )}

          {item.calendarItem.note && item.calendarItem.note.trim() && (
            <div className="day-detail-notes" style={{ marginTop: 4 }}>
              <div style={{ fontSize: 12, color: "#b0b0cc", marginBottom: 2 }}>
                {item.calendarItem.eventType === "go_by"
                  ? "Go By Note:"
                  : "Note:"}
              </div>
              <div
                style={{
                  fontSize: 12,
                  color: "rgba(255,255,255,0.6)",
                  paddingLeft: 4,
                  whiteSpace: "pre-wrap",
                }}
              >
                {item.calendarItem.note}
              </div>
            </div>
          )}

          {item.calNotes?.length > 0 && (
            <div className="day-detail-notes" style={{ marginTop: 4 }}>
              <div style={{ fontSize: 12, color: "#b0b0cc", marginBottom: 2 }}>
                Notes:
              </div>
              {item.calNotes.map((note, i) => (
                <div
                  key={i}
                  style={{
                    fontSize: 12,
                    color: "rgba(255,255,255,0.6)",
                    paddingLeft: 4,
                  }}
                >
                  {note}
                </div>
              ))}
            </div>
          )}

          <div className="day-detail-actions">
            {phone && (
              <a href={`tel:${formatPhoneForTel(phone)}`} title="Call">
                <PhoneIcon sx={{ fontSize: 20 }} />
              </a>
            )}
            {address && (
              <a
                href={getGoogleMapsUrl(address)}
                target="_blank"
                rel="noopener noreferrer"
                title="Navigate"
              >
                <MapIcon sx={{ fontSize: 20 }} />
              </a>
            )}
            {/* Drive time (registry §17.10). `raised`: this popup already sits
                above the calendar modal, so its dialog must clear it too —
                the same rung the enlarged photo uses below. The label is the
                job's name and start time, which is what a dispatcher means
                by "the job before the gap". */}
            {address && (
              <span data-tour="event-drive-time" className="day-detail-drive">
                <DriveTimeButton
                  variant="plain"
                  size="sm"
                  layer="raised"
                  place={makePlace(
                    `${getBlockLabelName(item)} · ${BAGroupTimeFormat(
                      item.calendarItem.startMs,
                      true,
                      false,
                      true
                    )}`,
                    item.customer
                  )}
                  openRef={tourDemo ? openCompareRef : undefined}
                  demoLegs={tourDemo ? DEMO_LEGS : undefined}
                />
              </span>
            )}
            {tourDemo && <DemoCompareAction onRun={() => openCompareRef.current?.()} />}
            {(item.invoice || item.quote) && (
              <button
                title={item.invoice ? "View Invoice" : "View Quote"}
                onClick={() => {
                  if (item.invoice) {
                    onViewInvoice(item.invoice);
                  } else {
                    onViewQuote(item.quote);
                  }
                }}
              >
                <DescriptionIcon sx={{ fontSize: 20 }} />
              </button>
            )}
            <button title="Edit Event" onClick={() => onEdit(item)}>
              <EditIcon sx={{ fontSize: 20 }} />
            </button>
            {customerId && (
              <button
                title="Open Customer Page"
                onClick={() => {
                  onClose();
                  navigate(`/bdr-office/clients/view-client/${customerId}`);
                }}
              >
                <PersonIcon sx={{ fontSize: 20 }} />
              </button>
            )}

            {/* Pushed to the right-hand end of the icon row. It is a STATE,
                not an action, so it reads as a checkbox rather than joining
                the row of icon buttons beside it. */}
            {asapEligible && (
              <span className="day-detail-asap" data-tour="event-avail-sooner">
                <BdrCheckbox
                  size="sm"
                  name="availableSooner"
                  label="Avail. Sooner"
                  checked={availableSooner}
                  onChange={handleAvailSoonerToggle}
                />
              </span>
            )}
          </div>

          {address && (
            <div className="day-detail-map-section">
              <iframe
                className="day-detail-map-iframe"
                src={getGoogleMapsEmbedUrl(address)}
                title="Job location map"
                loading="lazy"
              />
            </div>
          )}
        </div>

          {photos.length > 0 && (
            <div className="event-detail-photos">
              <div className="event-detail-photos-title">Door Photos</div>
              {photos.map((p, i) => {
                const full = p?.s3ImageUrl ?? p?.s3ThumbUrl;
                const thumb = p?.s3ThumbUrl ?? p?.s3ImageUrl;
                if (!full) return null;
                return (
                  <EventPhotoCell
                    key={p._id || i}
                    thumb={thumb}
                    full={full}
                    note={p?.note}
                    index={i}
                    onOpen={setEnlarge}
                  />
                );
              })}
            </div>
          )}
        </div>
      </div>

      <ImageModal
        modalOpen={!!enlarge}
        handleClose={() => setEnlarge(null)}
        imgSrc={enlarge?.src}
        caption={enlarge?.note}
        allowNoteUpdate={false}
        /* raised: this popup sits above the calendar, so the enlarged image
           has to clear it. Was a hand-picked zIndex of 1500. */
        layer="raised"
      />
    </>,
    document.body
  );
};

export default EventDetailPopup;
